<template>
    <div class="checkout-checkin-report">
        <div class="card shadow-sm border-0">

            <HeaderFilter
                title="Check-out x Check-in"
                @apply-filters="loadData"
            />

            <div class="card-body">
                <!-- Loading -->
                <div
                    v-if="loadingData"
                    class="text-center py-4"
                >
                    <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
                    <p class="mt-2">Carregando dados...</p>
                </div>

                <!-- Conteúdo Principal -->
                <div v-else>
                    <!-- Métricas Resumidas -->
                    <div class="metrics-summary mb-4">
                        <div class="row">
                            <div class="col-md-4">
                                <div class="metric-card">
                                    <div class="metric-value">{{ totalCheckouts }}</div>
                                    <div class="metric-label">Total Check-outs</div>
                                    <div class="metric-icon checkout">
                                        <i class="fas fa-sign-out-alt"></i>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="metric-card">
                                    <div class="metric-value">{{ totalCheckins }}</div>
                                    <div class="metric-label">Total Check-ins</div>
                                    <div class="metric-icon checkin">
                                        <i class="fas fa-sign-in-alt"></i>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="metric-card">
                                    <div class="metric-value">{{ completionRate }}%</div>
                                    <div class="metric-label">Taxa de Conclusão</div>
                                    <div class="metric-icon completion">
                                        <i class="fas fa-percentage"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Gráfico -->
                    <div class="chart-container mb-4">
                        <LineChart
                            v-if="renderChart && chartData.labels.length > 0"
                            :chart-data="chartData"
                            :options="chartOptions"
                            :height="300"
                        />
                        <div
                            v-else
                            class="empty-chart"
                        >
                            <i class="fas fa-chart-line fa-3x text-muted"></i>
                            <p class="text-muted mt-2">Nenhum dado disponível para o período selecionado</p>
                        </div>
                    </div>

                    <!-- Tabela de Dados -->
                    <div class="data-table">
                        <div class="table-responsive">
                            <table class="table table-hover">
                                <thead class="bg-light">
                                <tr>
                                    <th>Mês</th>
                                    <th class="text-center">Check-outs</th>
                                    <th class="text-center">Check-ins</th>
                                    <th class="text-center">Taxa de Conclusão</th>
                                    <th class="text-center">Diferença</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr
                                    v-for="(data, index) in tableData"
                                    :key="index"
                                >
                                    <td class="font-weight-bold">{{ data.monthName }}</td>
                                    <td class="text-center">
                                        <span class="badge badge-warning">{{ data.checkouts }}</span>
                                    </td>
                                    <td class="text-center">
                                        <span class="badge badge-success">{{ data.checkins }}</span>
                                    </td>
                                    <td class="text-center">
                                        <span
                                            class="badge"
                                            :class="getCompletionBadgeClass(data.completionRate)"
                                        >
                                            {{ data.completionRate }}%
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <span
                                            class="badge"
                                            :class="getDifferenceBadgeClass(data.difference)"
                                        >
                                            {{ data.difference > 0 ? '+' : '' }}{{ data.difference }}
                                        </span>
                                    </td>
                                </tr>
                                <tr v-if="tableData.length === 0">
                                    <td
                                        colspan="5"
                                        class="text-center text-muted py-4"
                                    >
                                        Nenhum dado encontrado para o período selecionado
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import LineChart from "@/components/Utils/Chart.js";
import vSelect from "vue-select";
import HeaderFilter from "./HeaderFilter.vue";
import {getMessage} from "../../composables/message";

export default {
    name: "CheckoutCheckinReport", // nome do componente

    components: {
        LineChart, // componente de gráfico de linha
        vSelect, // componente de select
        HeaderFilter
    },

    props: {
        globalFilters: {
            type: Object,
            default: () => ({}) // filtros globais passados pelo componente pai
        },
        loading: {
            type: Boolean,
            default: false // estado de carregamento externo
        }
    },

    data() {
        return {
            showLocalFilters: false, // controla exibição dos filtros locais
            loadingData: false, // controla carregamento dos dados
            renderChart: false, // controla renderização do gráfico

            // localFilters - filtros específicos deste relatório
            localFilters: {
                branch_id: null, // ID da filial
                retreat_date: "", // data de início
                devolution_date: "", // data final
            },

            // branches - lista de filiais
            branches: [
                {
                    id: null,
                    description: "TODAS AS FILIAIS",
                },
            ],

            // chartData - dados para o gráfico
            chartData: {
                labels: [], // labels dos meses
                datasets: [
                    {
                        label: "Check-out",
                        backgroundColor: "transparent",
                        borderColor: "#FFB22B",
                        borderWidth: 3,
                        pointBackgroundColor: "#FFB22B",
                        pointBorderColor: "#FFB22B",
                        pointRadius: 5,
                        pointHoverRadius: 7,
                        data: [], // dados de check-out
                    },
                    {
                        label: "Check-in",
                        backgroundColor: "transparent",
                        borderColor: "#1B9E77",
                        borderWidth: 3,
                        pointBackgroundColor: "#1B9E77",
                        pointBorderColor: "#1B9E77",
                        pointRadius: 5,
                        pointHoverRadius: 7,
                        data: [], // dados de check-in
                    },
                ],
            },

            // chartOptions - opções de configuração do gráfico
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'top',
                        labels: {
                            usePointStyle: true,
                            padding: 20
                        }
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        borderWidth: 1,
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        grid: {
                            color: 'rgba(0, 0, 0, 0.1)'
                        }
                    },
                    x: {
                        grid: {
                            display: false
                        }
                    }
                },
                interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                }
            },

            // tableData - dados para a tabela
            tableData: [],
        };
    },

    computed: {
        /**
         * Total de check-outs / Total checkouts
         */
        totalCheckouts() {
            return this.tableData.reduce((sum, item) => sum + (item.checkouts || 0), 0);
        },

        /**
         * Total de check-ins / Total checkins
         */
        totalCheckins() {
            return this.tableData.reduce((sum, item) => sum + (item.checkins || 0), 0);
        },

        /**
         * Taxa de conclusão geral / Overall completion rate
         */
        completionRate() {
            if (this.totalCheckouts === 0) return 0;
            return Math.round((this.totalCheckins / this.totalCheckouts) * 100);
        },
    },

    watch: {
        // Observa mudanças nos filtros globais / Watch global filters changes
        globalFilters: {
            handler() {
                this.loadData();
            },
            deep: true
        },

        // Observa mudanças no estado de loading externo / Watch external loading state
        loading(newVal) {
            if (!newVal) {
                this.loadData();
            }
        }
    },

    mounted() {
        this.loadData(); // carrega dados iniciais
    },

    methods: {
        /**
         * Carrega dados do relatório / Load report data
         */
        async loadData(params = null) {
            this.loadingData = true;
            this.renderChart = false;

            try {

                const api = this.$store.state.api + "dashboardTwo";

                const response = await this.$http.get(api, {params});

                this.processData(response.data);
                this.renderChart = true;

            } catch (error) {
                console.error('Erro ao carregar dados check-out/check-in:', error);
                const message = error.response.data.errors
                    ? getMessage(error.response.data.errors)
                    : error.response.data || 'Erro ao carregar dados';
                this.$message(null, message, "error");
            } finally {
                this.loadingData = false;
            }
        },

        /**
         * Processa dados recebidos da API / Process data received from API
         */
        processData(data) {
            // Atualiza dados da tabela / Update table data
            this.tableData = (data.table || []).map(item => ({
                ...item,
                completionRate: item.checkouts > 0
                    ? Math.round((item.checkins / item.checkouts) * 100)
                    : 0,
                difference: (item.checkins || 0) - (item.checkouts || 0)
            }));

            // Atualiza dados do gráfico / Update chart data
            this.chartData.labels = data.months || [];
            this.chartData.datasets[0].data = data.checkouts || [];
            this.chartData.datasets[1].data = data.checkins || [];
        },

        /**
         * Retorna classe CSS para badge de taxa de conclusão / Return CSS class for completion rate badge
         */
        getCompletionBadgeClass(rate) {
            if (rate >= 90) return 'badge-success';
            if (rate >= 70) return 'badge-warning';
            return 'badge-danger';
        },

        /**
         * Retorna classe CSS para badge de diferença / Return CSS class for difference badge
         */
        getDifferenceBadgeClass(difference) {
            if (difference > 0) return 'badge-success';
            if (difference < 0) return 'badge-danger';
            return 'badge-secondary';
        },
    },
};
</script>

<style scoped>
/* Estilo principal do relatório */
.checkout-checkin-report .card {
    border-radius: 12px;
    overflow: hidden;
}

.bg-gradient-success {
    background: linear-gradient(135deg, #1cc88a 0%, #13855c 100%);
}

.metric-icon.checkout {
    background: linear-gradient(135deg, #FFB22B 0%, #FF8008 100%);
}

.metric-icon.checkin {
    background: linear-gradient(135deg, #1B9E77 0%, #138B64 100%);
}

.metric-icon.completion {
    background: linear-gradient(135deg, #4e73df 0%, #224abe 100%);
}

/* Container do gráfico */
.chart-container {
    position: relative;
    height: 300px;
    background: white;
    border-radius: 8px;
    padding: 1rem;
    border: 1px solid #e3e6f0;
}

.empty-chart {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
}

/* Tabela de dados */
.data-table {
    background: white;
    border-radius: 8px;
    overflow: hidden;
}

.table {
    margin-bottom: 0;
}

.table thead th {
    border-top: none;
    font-weight: 600;
    color: #5a5c69;
    background-color: #f8f9fc;
}

.table tbody tr:hover {
    background-color: #f8f9fc;
}

/* Badges */
.badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
}

/* Formulários */
.form-label {
    font-weight: 600;
    color: #5a5c69;
    margin-bottom: 0.5rem;
}

.form-control {
    border-radius: 0.35rem;
    border: 1px solid #d1d3e2;
}

.form-control:focus {
    border-color: #1cc88a;
    box-shadow: 0 0 0 0.2rem rgba(28, 200, 138, 0.25);
}

/* Responsividade */
@media (max-width: 768px) {
    .metric-card {
        margin-bottom: 1rem;
    }

    .chart-container {
        height: 250px;
    }

    .metric-value {
        font-size: 1.5rem;
    }

    .table-responsive {
        font-size: 0.875rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Indicators/CheckoutCheckin.vue